import { Info, PanelRightOpen } from 'lucide-react';
import { AnimatePresence, motion, Variants } from 'motion/react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { buttonVariants } from 'ui';
import { cn } from 'utils/cn';
import { isInChromeExtension } from 'utils/proxyUtils';
import useDarkMode from '../../hooks/use-dark-mode';
import { screenshotStorage } from '../../services/screenshotStorage';
import { useAppSelector } from '../../store';
import { getSelectedSiteCollaborateInfo } from '../../store/siteInfoSlice';
import { getIsLoadingAi } from '../../store/tasksSlice';
import { getBrowseMode, getFullPageScreenshotId, getSidebarOpen, setBrowseMode, setSidebarOpen } from '../../store/uiSlice';
import { Menu } from './menu';
import ScanAnimation from './ScanAnimation';
import { Selector } from './selector';

interface SidebarProps {
  constraintsRef: React.RefObject<HTMLDivElement | null>;
  isLoadingTasks: boolean;
}

export const Sidebar = ({ constraintsRef, isLoadingTasks }: SidebarProps) => {
  const { t } = useTranslation('collaborate', { useSuspense: false });
  const dispatch = useDispatch();
  const browseMode = useAppSelector(getBrowseMode);
  const sidebarOpen = useAppSelector(getSidebarOpen);
  const isLoadingAi = useAppSelector(getIsLoadingAi)
  const [transformX, setTransformX] = useState(100)
  const buttonRef = useRef<HTMLDivElement>(null)
  const hideTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const siteCollaborateInfo = useAppSelector(getSelectedSiteCollaborateInfo);
  const [theme] = useDarkMode();
  const screenshotId = useAppSelector(getFullPageScreenshotId);
  const [screenshot, setScreenshot] = useState<string | null>(null)

  useEffect(() => {
    const fetchScreenshot = async () => {
      const screenshot = await screenshotStorage.getScreenshotBlobUrl(screenshotId || '');
      setScreenshot(screenshot);
    }
    fetchScreenshot();
  }, [screenshotId]);


  // This function dynamically adjusts the `transformX` state based on the mouse's position
  // relative to the button. It calculates the distance between the mouse and the button,
  // and updates the state to create a visual effect. If the mouse moves too far away,
  // a timeout resets the state after a delay.

  const handleMouseMove = useCallback((event: MouseEvent) => {
    const button = buttonRef.current;
    if (!button) return;

    const rect = button.getBoundingClientRect();
    const mouseX = event.clientX;
    const mouseY = event.clientY;

    // You can use vertical distance only, or full distance:
    const verticalDistance = Math.abs(mouseY - (rect.top + rect.height / 2));
    const horizontalDistance = Math.abs(mouseX - (rect.left + rect.width / 2));
    const maxDistance = 150;
    const minDistance = 60;
    const range = maxDistance - minDistance;

    const distance = Math.hypot(horizontalDistance, verticalDistance);

    if (distance < maxDistance) {
      if (hideTimeoutRef.current) {
        clearTimeout(hideTimeoutRef.current);
        hideTimeoutRef.current = null;
      }

      let newTransformX;
      if (distance < minDistance) {
        newTransformX = 0;
      } else {
        const progress = (distance - minDistance) / range;
        newTransformX = progress * 100;
      }

      setTransformX(newTransformX);
    } else {
      if (!hideTimeoutRef.current) {
        hideTimeoutRef.current = setTimeout(() => {
          setTransformX(100);
          hideTimeoutRef.current = null;
        }, 3000);
      }
    }
  }, []);


  // Attach and clean up mousemove event listener
  useEffect(() => {
    document.addEventListener("mousemove", handleMouseMove)
    return () => {
      document.removeEventListener("mousemove", handleMouseMove)
    }
  }, [handleMouseMove])

  // Set overlay visibility based on user guest status
  // useEffect(() => {
  //   setIsOverlayVisible(!!userData.isGuest);
  // }, [userData]);

  const toggleSidebar = () => {
    dispatch(setSidebarOpen(!sidebarOpen));
  };


  // const handleCloseOverlay = () => {
  //   setIsOverlayVisible(false);
  // };

  const handleBrowseModeToggle = () => {
    if (browseMode) {
      dispatch(setBrowseMode(false));
      dispatch(setSidebarOpen(true));
    }
  };

  // Animation variants
  const sidebarVariants: Variants = {
    open: {
      opacity: 1,
      x: 0,
      y: '-50%',
      top: '50%',
      right: 10,
      transition: {
        type: 'spring',
        duration: 0.2,
        stiffness: 100,
        damping: 13,
        mass: 0.9,
      },
    },
    closed: {
      opacity: 0,
      x: 320, // move fully offscreen (sidebar width + margin)
      y: '-50%',
      top: '50%',
      right: 10,
      transition: {
        duration: 0.2,
        type: 'spring',
        stiffness: 100,
        damping: 13,
        mass: 0.9,
      },
    },
  };

  const renderSidebar = () => (
    <motion.div
      key="right-sidebar"
      drag
      dragElastic={0.1}
      dragMomentum={false}
      dragConstraints={constraintsRef}
      className="fixed min-h-[550px] pointer-events-auto max-h-[85vh] w-[280px] rounded-2xl border border-border overflow-hidden flex flex-col"
      initial="closed"
      animate="open"
      exit="closed"
      variants={sidebarVariants}
      style={{
        boxShadow:
          '0 0 0 1px color-mix(in oklab, #0000330f, #f0f0f3 25%), 0 12px 60px #00000026, 0 16px 64px #00005506, 0 16px 36px -20px #00062e12',
      }}
    >
      <div className="absolute bg-card brightness-110 saturate-50 bg-opacity-90 backdrop-blur-lg h-full w-full z-[-2] left-0 top-0 rounded-2xl dark:saturate-100 dark:bg-opacity-100 dark:brightness-100"></div>


      <AnimatePresence>
        {isLoadingAi &&
          <ScanAnimation imageUrl={screenshot} />
        }
      </AnimatePresence>
      {/* <AnimatePresence>
        {isOverlayVisible && (
          <motion.div
            initial={{ opacity: 1 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            key="overlay"
            style={{
              position: 'absolute',
              top: 0,
              right: 0,
              width: '100%',
              height: '100%',
            }}
          >
            <GuestOverlay
              name={userData!.name}
              avatar={userData?.avatar || ""}
              onClose={handleCloseOverlay}
            />
          </motion.div>
        )}
      </AnimatePresence> */}
      <Menu />
      <Selector isLoadingTasks={isLoadingTasks} />
      {!isInChromeExtension() && (
        <div className="flex items-center gap-1 justify-center mb-2 mt-auto text-textcolor">
          <Info size={14} />
          <p className="text-xs">
            {t('sidebar.info', 'Even better with our ')}
            <a href='https://chromewebstore.google.com/detail/atarim-visual-website-col/ekpmbeilnaahlccpgjchjjoonecikehf'
              target="_blank"
              className={cn(
                buttonVariants({
                  variant: 'link',
                  size: 'link',
                }),
                'text-xs font-normal'
              )}>{t('chrome_extension', 'Chrome Extension')}</a>
          </p>
        </div>
      )}
    </motion.div>
  );

  const renderHamburger = () => (
    <AnimatePresence>
      <motion.div
        ref={buttonRef}
        className="fixed right-0 top-1/3 z-50 transition-all duration-300 ease-out pointer-events-auto"
        style={{
          transform: `translateX(${transformX}%) translateY(-50%)`,
        }}
      >
        <button
          className="rounded-l-xl p-1.5 h-16 bg-background border border-border shadow-lg hover:shadow-sm"
          onClick={browseMode ? handleBrowseModeToggle : toggleSidebar}
          aria-label={t('sidebar.expand', 'Expand sidebar')}
        >
          {isInChromeExtension() ? (
            <img src={
              siteCollaborateInfo?.result?.setting?.whitelabel.favicon.includes('atarim_icon.svg') && theme === 'dark' ?
                'https://atarim-wpfeedback-image.s3.eu-west-1.amazonaws.com/media/atarim_logo_icon_white.svg' :
                siteCollaborateInfo?.result?.setting?.whitelabel.favicon
            } alt="atarim-icon" className="h-6 w-6" />
          ) : (
            <PanelRightOpen size={20} className='text-textcolor' />
          )}
        </button>
      </motion.div>
    </AnimatePresence>
  );

  return (
    <>
      <AnimatePresence mode="wait">
        {!browseMode && sidebarOpen ? renderSidebar() : null}
      </AnimatePresence>

      {/* Always render hamburger when sidebar is closed or in browse mode */}
      {(!sidebarOpen || browseMode) && renderHamburger()}
    </>
  );
};